import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Container, SectionLabel, CampaignCard, CardGridSkeleton, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import { Crown, Receipt, ShieldCheck, Repeat, Frown } from "lucide-react";
import { Brand } from "@/components/Brand";

const PERKS = [
  { icon: Receipt, title: "Branded tax receipts", desc: "Every donor gets a professional, tax-deductible receipt with the org's logo & EIN." },
  { icon: ShieldCheck, title: "Reviewed & verified", desc: "Pro organizers pass review and get priority handling." },
  { icon: Repeat, title: "Built for retention", desc: "Recurring-donor tools keep supporters giving month after month." },
];

export default function ProDirectory() {
  const [campaigns, setCampaigns] = useState(null);

  useEffect(() => {
    api.get("/campaigns", { params: { status: "live", pro_only: true, limit: 60 } })
      .then((r) => setCampaigns(r.data))
      .catch(() => setCampaigns([]));
  }, []);

  return (
    <div className="bg-white">
      <Seo title="FightFund Pro Organizations" description="Discover verified FightFund Pro organizers and nonprofits — branded receipts, reviewed campaigns, and donor-first tools." />

      <section className="border-b border-ff-border bg-ff-blue text-white">
        <Container className="py-14">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#FEF3C7] px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-[#92400E]">
            <Crown className="h-3.5 w-3.5" /> <Brand /> Pro
          </div>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-extrabold tracking-tight sm:text-5xl">
            Give to a verified Pro organization
          </h1>
          <p className="mt-3 max-w-2xl text-base text-white/75">
            Pro organizers invest in transparency — branded tax-deductible receipts, reviewed campaigns, and donor-first tools. Support one below.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {PERKS.map((p) => (
              <div key={p.title} className="rounded-xl bg-white/5 p-4" data-testid={`pro-perk-${p.title.split(" ")[0].toLowerCase()}`}>
                <p.icon className="h-5 w-5 text-ff-amber" />
                <div className="mt-2 font-heading text-sm font-bold">{p.title}</div>
                <div className="mt-0.5 text-xs text-white/70">{p.desc}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <Container className="py-12">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <SectionLabel>Pro directory</SectionLabel>
            <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Pro campaigns</h2>
          </div>
          <Button asChild variant="outline" className="border-ff-border text-ff-blue hover:border-ff-red">
            <Link to="/browse" data-testid="pro-directory-browse-all">Browse all campaigns</Link>
          </Button>
        </div>

        {campaigns === null ? (
          <CardGridSkeleton count={6} />
        ) : campaigns.length === 0 ? (
          <EmptyState icon={Frown} title="No Pro campaigns yet"
            message="Check back soon — or become the first Pro organizer."
            action={<Link to="/nonprofit/dashboard" className="rounded-lg bg-ff-action px-4 py-2 text-sm font-semibold text-white">Become Pro</Link>}
            testid="pro-directory-empty" />
        ) : (
          <>
            <div className="mb-5 text-sm text-ff-medium" data-testid="pro-directory-count">{campaigns.length} Pro campaign{campaigns.length !== 1 ? "s" : ""}</div>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-3" data-testid="pro-directory-grid">
              {campaigns.map((c) => <CampaignCard key={c.id} c={c} />)}
            </div>
          </>
        )}
      </Container>
    </div>
  );
}
